import { getTenantId, setLocalTenantId } from "@/lib/storeManager";
import { createContext, ReactNode, useEffect, useState } from "react";

interface TenantContextType {
  tenantId: string | null;
  setTenantId: (id: string) => void;
  clearTenantId: () => void;
}

const TenantContext = createContext<TenantContextType | undefined>(undefined);

interface TenantProviderProps {
  children: ReactNode;
}

export const TenantProvider: React.FC<TenantProviderProps> = ({ children }) => {
  const [tenantId, setTenantIdState] = useState<string | null>(() => {
    const envTenantId = (import.meta.env.VITE_X_TENANTID ?? "")
      .toString()
      .replace(/^"|"$/g, "")
      .trim();
    if (envTenantId) {
      return envTenantId;
    }
    const storedTenantId = getTenantId();
    return storedTenantId;
  });

  const setTenantIdHandler = (id: string) => {
    setLocalTenantId(id);
    setTenantIdState(id);
  };

  const clearTenantId = () => {
    localStorage.removeItem("tenantId");
    setTenantIdState(null);
  };

  // Persist tenant to localStorage when set (e.g. from env fallback) so API requests get X-Tenant-Id
  useEffect(() => {
    if (tenantId) {
      setLocalTenantId(tenantId);
    }
  }, [tenantId]);

  return (
    <TenantContext.Provider
      value={{
        tenantId,
        setTenantId: setTenantIdHandler,
        clearTenantId,
      }}
    >
      {children}
    </TenantContext.Provider>
  );
};

// Export the context for use in other files
export { TenantContext };
